import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ReceiptItemProductCandidate} from "../../models/receipt";
import {NgClass} from "@angular/common";
import {ReceiptService} from "../../services/receipt/receipt.service";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faCheck, faXmark} from "@fortawesome/free-solid-svg-icons";
import {ProxyImageDirective} from "../../directives/proxy-image.directive";
import {PricePipe} from "../../pipes/price.pipe";
import {UiButtonComponent} from "../../shared/ui";
import { AppLogger, AppLoggerService } from '../../core/logging';

@Component({
  selector: 'app-product-candidate-selector',
  imports: [
    FaIconComponent,
    ProxyImageDirective,
    PricePipe,
    NgClass,
    UiButtonComponent
  ],
  templateUrl: './product-candidate-selector.component.html',
  styleUrl: './product-candidate-selector.component.scss'
})
export class ProductCandidateSelectorComponent {

  private readonly log: AppLogger;

  @Input() rawProductName: string = '';
  @Input() receiptId: number = 0;
  @Input() receiptItemId: number = 0;
  @Input() receiptItemPricePerUnit: string = '';
  @Input() candidates: ReceiptItemProductCandidate[] = [];
  @Input() selectedCandidateId: number | null = null;
  @Output() selectionChanged = new EventEmitter<number>();
  @Output() searchRequested = new EventEmitter<{receiptId: number, receiptItemId: number, rawProductName: string}>();
  @Input() allowSearch: boolean = true;

  /** When true, the inline catalog search UI is currently shown (so we hide the "Search for Products" button). */
  @Input() isSearchOpen: boolean = false;

  protected readonly faCheck = faCheck;

  constructor(private receiptService: ReceiptService, private appLogger: AppLoggerService) {
    this.log = this.appLogger.createForClass(ProductCandidateSelectorComponent);
  }

  isExactPriceMatch(candidate: ReceiptItemProductCandidate): boolean {
    return candidate.price_delta_pct === "0";
  }

  isPriceWithinTolerance(candidate: ReceiptItemProductCandidate): boolean {
    return candidate.price_within_tol;
  }

  isPriceOutOfTolerance(candidate: ReceiptItemProductCandidate): boolean {
    return !candidate.price_within_tol;
  }

  resetSelection() {
    this.selectCandidate(0);
  }

  selectCandidate(candidateId: number) {
    this.receiptService.selectProductCandidate(this.receiptId, this.receiptItemId, candidateId).subscribe({
      next: () => {
        this.selectionChanged.emit(candidateId);
      },
      error: (error) => {
        this.log.error('Error selecting product candidate', error, { receiptId: this.receiptId, receiptItemId: this.receiptItemId, candidateId });
      }
    });
  }

  openCatalogSearch() {
    this.searchRequested.emit({
      receiptId: this.receiptId,
      receiptItemId: this.receiptItemId,
      rawProductName: this.rawProductName
    });
  }

  getListItemClasses(candidate: ReceiptItemProductCandidate, isSelected: boolean): string {
    if (isSelected) {
      return 'p-2 border border-success rounded-lg bg-success/5';
    }
    return 'p-2 border border-base-300 rounded-lg bg-base-100';
  }

  isCandidateSelected(candidate: ReceiptItemProductCandidate): boolean {
    if (this.selectedCandidateId != null) {
      return candidate.id === this.selectedCandidateId;
    }
    return candidate.is_selected ?? false;
  }

  isOneCandidateSelected(): boolean {
    if (this.selectedCandidateId != null) return true;
    return this.candidates.some(c => c.is_selected);
  }


  protected readonly faXmark = faXmark;
}
